import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ContactDto } from '@/api/generated';
import { useContacts, useDeleteContact } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { hueForName } from '@/lib/format';
import styles from './ContactDetail.module.css';
import { ContactEditDialog } from './ContactFormDialog';

// 2026-05-17 — the ROLE_PHASES + "Werkt aan fases" + "Financieel overzicht"
// fixtures were Phase 1 design-handoff placeholders. There's no API concept
// for per-contact phase associations or per-contact financial totals today,
// so they rendered fake data on every contact. Removed.

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const contacts = useContacts({ pageSize: 50 });

  return (
    <AppFrame>
      <QueryBoundary query={contacts} loading="Contact laden…">
        {(page) => {
          const contact = page.items.find((c) => c.id === id);
          if (!contact) {
            return (
              <div role="alert" className="mono c-bad">
                Contact niet gevonden.{' '}
                <Link to="/contacten" className={styles.crumbLink}>
                  Terug naar adresboek
                </Link>
              </div>
            );
          }
          return <ContactDetailBody contact={contact} />;
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function ContactDetailBody({ contact }: { contact: ContactDto }) {
  const hue = hueForName(contact.name);
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMutation = useDeleteContact();

  return (
    <>
      <div className={`row gap-6 ${styles.crumb}`}>
        <Link to="/contacten" className={styles.crumbLink}>
          Contacten
        </Link>
        <Icon name="chevR" size={11} />
        <span>{contact.role}</span>
        <Icon name="chevR" size={11} />
        <span className={styles.crumbCurrent}>{contact.company ?? contact.name}</span>
      </div>

      <Card className={styles.headerCard}>
        <div className={`row ${styles.headerRow}`}>
          <Avatar name={contact.name} size={64} hue={hue} />
          <div className="flex-1">
            <div className={`row gap-10 ${styles.titleRow}`}>
              <h1 className={styles.name}>{contact.name}</h1>
              <Pill kind="ok">{contact.role}</Pill>
            </div>
            <div className={styles.subTitle}>{contact.company ?? '—'}</div>
            <div className={`row gap-16 ${styles.contactRow}`}>
              {contact.phone && (
                <div className="row gap-6">
                  <Icon name="phone" size={13} color="var(--ink-3)" />
                  <span className={`mono ${styles.contactDetail}`}>{contact.phone}</span>
                </div>
              )}
              {contact.email && (
                <div className="row gap-6">
                  <Icon name="mail" size={13} color="var(--ink-3)" />
                  <span className={`mono ${styles.contactDetail}`}>{contact.email}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`col gap-6 ${styles.headerActions}`}>
            {contact.phone ? (
              <a
                href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                className={styles.telLink}
                aria-label={`Bel ${contact.name}`}
              >
                <Icon name="phone" size={14} /> Bel
              </a>
            ) : (
              <Btn disabled aria-disabled="true">
                <Icon name="phone" size={14} /> Bel
              </Btn>
            )}
            <Btn onClick={() => setEditOpen(true)}>
              <Icon name="edit" size={14} /> Bewerk
            </Btn>
            <Btn
              variant="ghost"
              className="c-bad"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteMutation.isPending}
            >
              <Icon name="trash" size={14} /> Verwijder
            </Btn>
          </div>
        </div>
      </Card>
      <ContactEditDialog open={editOpen} onOpenChange={setEditOpen} contact={contact} />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`${contact.name} verwijderen?`}
        description="Het contact wordt definitief verwijderd uit het adresboek."
        confirmLabel="Verwijder"
        destructive
        pending={deleteMutation.isPending}
        onConfirm={async () => {
          try {
            await deleteMutation.mutateAsync(contact.id);
            setConfirmDelete(false);
            navigate('/contacten');
          } catch {
            // toast surfaced by the hook; keep dialog open so user can retry.
          }
        }}
      />
    </>
  );
}
